// Justificación de tamaño (policies/code.md §SOLID) — 509 líneas operativas, TEMPORAL.
// Este archivo NO es un motor legítimo: es el panel de WhatsApp que se pasó del cap de 500 en
// commits recientes de la feature (que entraron sin correr el ratchet). El split real —
// extraer composer / lista de chats / bloques de mensajes / top bar bajo 500 — está agendado
// como Punto 5.16 de `.claude/plans/frontend-restructuring/05-remediation.md`. Esta entrada del
// allowlist y este comentario se BORRAN en ese punto. TODO Phase 5.16.
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useWindowEvent } from "../../../../../shared/hooks/useWindowEvent";
import {
  X,
  User,
} from "lucide-react";
// eslint-disable-next-line no-restricted-imports
import { apiClient } from "../../../../../shared/api/apiClient";
import "./WhatsPanel.css";
import WhatsConnectQrView from "./WhatsConnectQrView";
import WhatsChatList from "./WhatsChatList";
import WhatsConversationHeader from "./WhatsConversationHeader";
import WhatsMessageList from "./WhatsMessageList";
import WhatsMeetPopup from "./WhatsMeetPopup";
import WhatsTaskPopup from "./WhatsTaskPopup";
import WhatsMessageComposer from "./WhatsMessageComposer";
import WhatsSettingsView from "./WhatsSettingsView";
import WhatsPanelTopBar from "./WhatsPanelTopBar";
import WhatsRightPanel from "./WhatsRightPanel";
import WhatsSemanticTagBadges from "./WhatsSemanticTagBadges";
import WhatsForwardDrawer from "./WhatsForwardDrawer";
import { useWhatsMessageActions } from "./useWhatsMessageActions";
import { spacesApi } from "../../../../spaces/api";
import ComponentTaskHoy from "../../../../tasks/components/ComponentTaskHoy";
import { useTasksSocketSync } from "../../../../tasks/hooks/useComponentTaskData";

import {
  useWhatsAppStatus, useWhatsAppChats, useWhatsAppMessages,
  useConnectWhatsApp, useSendWhatsAppMessage, useSendWhatsAppMedia,
  useUploadWhatsAppShortlink, useMarkWhatsAppChatViewed, usePresenceMap,
  useWhatsAppSocketSync, useZeroWhatsAppChatUnread,
} from "../../../../client/hooks/useWhatsAppData";
import { clientApi } from "../../../../client/api";
import { semanticTagsMap } from "../../../lib/semanticTags";
import { filterAndSortChats, mergeLinkedChats, isGroupJid } from "../../../lib/sortChats";
import { useWhatsActiveFilter } from "../../../hooks/useWhatsActiveFilter";
// Referencia estable para el fallback de listas antes de que las queries resuelvan -- `[]` inline
// en cada render rompería la identidad de la que dependen `filteredChats`/`messages` (mismo
// criterio que `features/client/components/WhatsAppPanel.jsx:16`).
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: dispara el sync de hash al montar/cambiar `chats` (ya documentado arriba, Fase 5.16), anima la barra de progreso del QR y resetea la paginación de mensajes al elegir un chat.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const EMPTY_LIST = [];

const WhatsPanel = ({ onClose, user }) => {
  const mockToast = { error: console.error, success: console.log, info: console.info, notify: console.log, prompt: () => {} };
  // 02.7-settings.md paso a (parte 3) -- migra los 3 `setInterval` propios (status/chats/
  // mensajes) al hook compartido de `features/client/` (§0.1). `useWhatsAppSocketSync()` ya cubre
  // los 5 eventos reales del backend -- no hace falta un `useSocket(socketEvents)` local aquí.
  useWhatsAppSocketSync();
  useTasksSocketSync();
  const zeroChatUnread = useZeroWhatsAppChatUnread();

  const [qrProgress, setQrProgress] = useState(100);
  const hasRequestedRef = useRef(false);

  const [isPinned, setIsPinned] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const panelRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);
  // `olderMessages` -- páginas más viejas que la "página 0" reactiva de `useWhatsAppMessages`
  // (§4 decisión (b): no convertir a `useInfiniteQuery`, mantener el offset/scroll-anchor propio
  // del container, más simple y no le cambia nada a `WhatsAppPanel.jsx`). Se resetea a `[]` cada
  // vez que cambia `selectedChat`.
  const [olderMessages, setOlderMessages] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [priorityUpdateTick, setPriorityUpdateTick] = useState(0);

  const statusQuery = useWhatsAppStatus();
  const status = statusQuery.data?.status || "disconnected";
  const qrCodeData = statusQuery.data?.qr || null;

  const chatsQuery = useWhatsAppChats({ enabled: status === "connected" });
  // Integración main→dev (catch-up F): fusiona los chats vinculados (`linked_to_jid`) dentro de
  // su chat padre antes de filtrar/ordenar.
  const chats = React.useMemo(
    () => mergeLinkedChats(chatsQuery.data?.chats || EMPTY_LIST),
    [chatsQuery.data?.chats],
  );

  useEffect(() => {
    if (selectedChat) {
      const updatedChat = chats.find(c => c.chat_jid === selectedChat.chat_jid);
      if (updatedChat && (updatedChat.name !== selectedChat.name || updatedChat.avatar_url !== selectedChat.avatar_url || updatedChat.priority !== selectedChat.priority)) {
        setSelectedChat(updatedChat);
      }
    }
  }, [chats, selectedChat]);

  const loading = chatsQuery.isLoading;

  const messagesQuery = useWhatsAppMessages(selectedChat?.chat_jid);
  const firstPageMessages = messagesQuery.data?.messages || EMPTY_LIST;
  const loadingMessages = messagesQuery.isLoading;
  // Concatena páginas viejas (offset creciente, cargadas una vez, no cambian) + la "página 0"
  // reactiva -- ambas en el mismo orden DESC (más reciente primero) que ya usaba este archivo, ver
  // nota de `handleScroll`/`fetchOlderMessages` más abajo. Dedup por `msg_id`: si un mensaje nuevo
  // desplazó la ventana de la página 0 justo cuando se pidió una página vieja, evita duplicarlo
  // (§4's edge case de reconciliación, documentado también en el checklist del slice).
  const baseMessages = React.useMemo(() => {
    if (olderMessages.length === 0) return firstPageMessages;
    const seen = new Set(firstPageMessages.map(m => m.msg_id));
    return [...olderMessages.filter(m => !seen.has(m.msg_id)), ...firstPageMessages];
  }, [olderMessages, firstPageMessages]);

  const presences = usePresenceMap();

  const connectMutation = useConnectWhatsApp();
  const sendMutation = useSendWhatsAppMessage(selectedChat?.chat_jid);
  const sendMediaMutation = useSendWhatsAppMedia(selectedChat?.chat_jid);
  const uploadShortlinkMutation = useUploadWhatsAppShortlink();
  const markViewedMutation = useMarkWhatsAppChatViewed();
  const [isUploading, setIsUploading] = useState(false);
  const sending = sendMutation.isPending || sendMediaMutation.isPending || uploadShortlinkMutation.isPending || isUploading;

  // Burbuja "enviando..." derivada de la mutación (§4) -- reemplaza el `tempMsgId` hand-built +
  // matching manual contra el eco del socket que tenía este archivo (2 caminos de reconciliación
  // independientes podían pisarse entre sí, la misma clase de bug que `client`'s hook ya evita).
  const messages = React.useMemo(() => {
    if (!sendMutation.isPending) return baseMessages;
    return [...baseMessages, {
      msg_id: '__pending__',
      chat_jid: selectedChat?.chat_jid,
      from_me: true,
      text_content: sendMutation.variables?.text,
      status: 'pending',
      timestamp: new Date().toISOString(),
    }];
  }, [baseMessages, sendMutation.isPending, sendMutation.variables, selectedChat?.chat_jid]);

  // 02.7-settings.md §0.4/paso b -- antes escuchaba `window: 'chat_priority_updated'`.
  // `UserProfilePanel` ahora llama este callback directo (hijo→padre, ver su propio comentario).
  const handlePriorityChange = () => setPriorityUpdateTick(prev => prev + 1);

  const [showMeetPopup, setShowMeetPopup] = useState(false);
  const [showTaskPopup, setShowTaskPopup] = useState(false);
  const [meetSearchQuery, setMeetSearchQuery] = useState("");
  const [meetMembers, setMeetMembers] = useState([]);
  const [selectedMeetUser, setSelectedMeetUser] = useState(null);
  
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (user?.currentSpace?.id && meetMembers.length === 0) {
      // 02.7-settings.md §5 -- reusa `spacesApi.fetchMembers` (ya envuelve exactamente
      // `/api/space/:id/members`) en vez del `fetch` crudo que tenía este archivo.
      spacesApi.fetchMembers(user.currentSpace.id)
        .then((membersArray) => {
          if (membersArray.length > 0) {
            const sortedMembers = [...membersArray].sort((a, b) => {
              if (String(a.id) === String(user.id)) return -1;
              if (String(b.id) === String(user.id)) return 1;
              return 0;
            });
            setMeetMembers(sortedMembers);
            const me = sortedMembers.find(m => String(m.id) === String(user.id)) || sortedMembers[0];
            setSelectedMeetUser(me);
          }
        })
        .catch(err => console.error(err));
    }
  }, [user, meetMembers.length]);

  const activeSemanticTags = React.useMemo(() => {
    if (!newMessage || newMessage.trim() === "") return [];
    const combinedText = newMessage.toLowerCase();
    const detectedTags = [];
    Object.keys(semanticTagsMap).forEach(key => {
      const tagInfo = semanticTagsMap[key];
      const hasWord = tagInfo.words.some(word => {
        const regex = new RegExp(`(^|[^a-záéíóúñ])(${word.toLowerCase()})([^a-záéíóúñ]|$)`, 'i');
        return regex.test(combinedText);
      });
      if (hasWord) {
        detectedTags.push(tagInfo);
      }
    });
    return detectedTags;
  }, [newMessage]);

  const { activeFilter, changeFilter } = useWhatsActiveFilter(user?.dbId || user?.id);
  const inputRef = useRef(null);
  // epic B2 (integración main→dev): responder/reenviar + panel derecho (perfil / preview de
  // media) + su ancho redimensionable. Fuera del container por el cap de líneas.
  const msgActions = useWhatsMessageActions();
  const { activeRightPanel, setActiveRightPanel } = msgActions;
  const scrollContainerRef = useRef(null);
  const chatListScrollRef = useRef(null);

  // URL Hash -> State Sync. Fase 5.16: `window.addEventListener('hashchange')` → `useWindowEvent`
  // (suscripción compartida a nivel de módulo). El handler se llama también una vez al montar /
  // cuando cambia `chats`, en un efecto aparte.
  const handleHash = useCallback(() => {
      const hash = window.location.hash;
      if (!hash.startsWith('#whats')) return;

      if (hash === '#whats' || hash === '#whats/inbox') {
        setSelectedChat(null);
        setActiveRightPanel(null);
      } else if (hash.startsWith('#whats/chat/')) {
        const parts = hash.split('/');
        const phone = parts[2];
        const subRoute = parts[3];

        if (phone && chats.length > 0) {
          // Find chat by phone number
          const targetChat = chats.find(c => c.chat_jid.startsWith(phone + '@'));
          if (targetChat) {
            setSelectedChat(prev => {
              if (!prev || prev.chat_jid !== targetChat.chat_jid) return targetChat;
              return prev;
            });
            
            if (subRoute === 'profile') {
              setActiveRightPanel(prev => {
                if (!prev || prev.type !== 'user') return { type: 'user', id: 'user', label: 'Perfil del Usuario' };
                return prev;
              });
            } else {
              setActiveRightPanel(null);
            }
          }
        }
      }
  }, [chats, setSelectedChat, setActiveRightPanel]);

  useWindowEvent('hashchange', handleHash);
  useEffect(() => { handleHash(); }, [handleHash]);

  // State -> URL Hash Sync
  useEffect(() => {
    if (!window.location.hash.startsWith('#whats')) return;

    let newHash = '#whats/inbox';
    if (selectedChat) {
      const phone = selectedChat.chat_jid.split('@')[0];
      newHash = `#whats/chat/${phone}`;
      if (activeRightPanel?.id === 'user' || activeRightPanel?.type === 'user') {
        newHash += '/profile';
      }
    }
    
    // Only update if it's different to prevent infinite loops with hashchange event
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
    }
  }, [selectedChat, activeRightPanel]);

  // Auto-scroll chat list when filters change or when returning to list
  useEffect(() => {
    if (chatListScrollRef.current && !selectedChat) {
      chatListScrollRef.current.scrollTop = 0;
    }
  }, [activeFilter, searchTerm, selectedChat]);
  const prevScrollHeightRef = useRef(0);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        document.body.contains(e.target) &&
        !isPinned
      ) {
        const isNavBtn = e.target.closest(".nav-item");
        const isPortal = e.target.closest(".task-panel-overlay") || e.target.closest(".tox") || e.target.closest("[role='dialog']");
        if (!isNavBtn && !isPortal) {
          onClose();
        }
      }
    };
    const tid = setTimeout(
      () => document.addEventListener("mousedown", handleClickOutside),
      100,
    );
    return () => {
      clearTimeout(tid);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPinned, onClose]);

  const handleConnectClick = () => connectMutation.mutate();

  // Auto-generar QR en la primera carga si está desconectado -- mismo guard que
  // `WhatsAppDashboard.jsx` (02.7-settings.md paso a parte 2): exige `statusQuery.isSuccess` antes
  // de considerar disparar, para no pisar un status real (`qr_ready`/`connected`) con el
  // resultado de `connect` si esa mutación resuelve más rápido que la query de status en la
  // primera renderizada (bug real encontrado y corregido en la parte 2 de este mismo paso).
  useEffect(() => {
    if (statusQuery.isSuccess && status === "disconnected" && !qrCodeData && !hasRequestedRef.current) {
      hasRequestedRef.current = true;
      connectMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQuery.isSuccess, status, qrCodeData, connectMutation.mutate]);

  useEffect(() => {
    if (qrCodeData) {
      setQrProgress(100);
      const startTime = Date.now();
      const duration = 20000; // 20 seconds
      
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setQrProgress(remaining);
      }, 100);

      return () => clearInterval(interval);
    }
  }, [qrCodeData]);

  // Carga una página más vieja (offset > 0) directo con `clientApi`, fuera de la cache del hook
  // (§4 decisión (b)) -- `useWhatsAppMessages` solo cubre la página 0, reactiva vía socket.
  const fetchOlderMessages = async (nextOffset) => {
    if (!selectedChat) return;
    setLoadingMore(true);
    try {
      const data = await clientApi.fetchWhatsAppMessages(selectedChat.chat_jid, { offset: nextOffset });
      if (scrollContainerRef.current) {
        prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
      }
      setOlderMessages((prev) => [...data.messages, ...prev]);
      if (data.messages.length < 50) setHasMore(false);
    } catch (err) {
      console.error("Error fetching older messages:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Al seleccionar un chat: resetea la paginación propia y llama a `markWhatsAppChatViewed` --
  // precondición real para que `whatsapp-presence` dispare para este jid en el backend (§0.1/§4),
  // no una llamada cosmética de "marcar como leído" como parecía en el `fetch(.../view)` que
  // reemplaza.
  useEffect(() => {
    if (selectedChat) {
      setOffset(0);
      setHasMore(true);
      setOlderMessages([]);
      markViewedMutation.mutate(selectedChat.chat_jid);
    }
    // `markViewedMutation.mutate` (no el objeto de la mutación completo) -- `mutate` es estable
    // entre renders; el objeto no lo es, y como este efecto siempre llama `setState` (no tiene un
    // guard de "solo una vez" como el de arriba), depender del objeto completo aquí causaba un
    // loop infinito de renders (bug real encontrado corriendo los tests de este mismo commit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat?.chat_jid, markViewedMutation.mutate]);

  React.useLayoutEffect(() => {
    if (
      offset === 0 &&
      !loadingMessages &&
      !loadingMore &&
      scrollContainerRef.current
    ) {
      // Use scrollHeight to robustly scroll to the very bottom
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      }, 100);
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      }, 500);
    } else if (
      offset > 0 &&
      prevScrollHeightRef.current > 0 &&
      scrollContainerRef.current
    ) {
      const newScrollHeight = scrollContainerRef.current.scrollHeight;
      scrollContainerRef.current.scrollTop +=
        newScrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [messages, loadingMessages, offset, loadingMore, selectedChat?.chat_jid]);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop } = scrollContainerRef.current;
      if (scrollTop === 0 && hasMore && !loadingMore && !loadingMessages) {
        const nextOffset = offset + 50;
        setOffset(nextOffset);
        fetchOlderMessages(nextOffset);
      }
    }
  };

  // §4 -- switch deliberado al patrón de `client`'s hook (`WhatsAppPanel.jsx`): sin escritura
  // manual de un mensaje optimista en la cache, la burbuja "enviando..." sale de
  // `sendMutation.isPending`/`variables` (ver el `useMemo` de `messages` de arriba). Guard de
  // envío portado de `WhatsAppPanel.jsx:handleSend` -- el patrón de burbuja pendiente solo evita
  // duplicados/pérdidas *junto con* este guard, no por separado.
  const handleTaskFromMessage = useCallback((msg) => {
    const rawText = (msg.text_content || "").trim();
    const prefix = rawText.toLowerCase().startsWith("tarea:") ? "" : "tarea: ";
    const formattedText = `${prefix}${rawText}`;
    setNewMessage(formattedText);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.selectionStart = inputRef.current.selectionEnd = formattedText.length;
      }
    }, 50);
  }, []);

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (sending) return;

    if (droppedFiles.length > 0) {
      setIsUploading(true);
      let captionText = newMessage.trim();
      let linksToAppend = [];
      
      try {
        for (let i = 0; i < droppedFiles.length; i++) {
          const { file, intent } = droppedFiles[i];
          const formData = new FormData();
          formData.append('file', file);

          const progressHandler = (progressEvent) => {
             const fileProgress = (progressEvent.loaded * 100) / progressEvent.total;
             setUploadProgress(Math.round(((i * 100) + fileProgress) / droppedFiles.length));
          };

          if (intent === 'attachment') {
            if (i === 0 && captionText) {
               formData.append('caption', captionText);
            }
            await sendMediaMutation.mutateAsync({ formData, onUploadProgress: progressHandler });
          } else if (intent === 'link') {
            const data = await uploadShortlinkMutation.mutateAsync({ formData, onUploadProgress: progressHandler });
            linksToAppend.push(data.link);
          }
        }
        
        const hasAttachment = droppedFiles.some(f => f.intent === 'attachment');
        if (linksToAppend.length > 0) {
           let textToSend = hasAttachment ? linksToAppend.join('\n') : (captionText ? `${captionText}\n\n${linksToAppend.join('\n')}` : linksToAppend.join('\n'));
           await sendMutation.mutateAsync({ text: textToSend, ...msgActions.replyPayload });
        }
        
        setDroppedFiles([]);
        setNewMessage('');
        setUploadProgress(0);
        msgActions.clearReply();
        setTimeout(() => inputRef.current?.focus(), 10);
      } catch (err) {
         setUploadProgress(0);
         console.error(err);
      } finally {
        setIsUploading(false);
      }
    } else {
      const text = newMessage.trim();
      if (!text || !selectedChat) return;
      setNewMessage("");
      sendMutation.mutate({ text, ...msgActions.replyPayload }, {
        onSuccess: () => {
          msgActions.clearReply();
          setTimeout(() => inputRef.current?.focus(), 10);
        },
      });
    }
  };

  const handleAttachClick = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.onchange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        const newFiles = Array.from(e.target.files).map(file => ({ file, intent: 'attachment' }));
        setDroppedFiles(prev => [...prev, ...newFiles]);
      }
    };
    fileInput.click();
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    // Optimista: pone `unread_count: 0` en la cache compartida (Fase 5.16: vía el hook, sin
    // `useQueryClient` directo en el componente).
    zeroChatUnread(chat.chat_jid);
    setActiveRightPanel(null);
  };

  // §0.4: "Flow" lee la prioridad de `localStorage` -- ver `lib/sortChats.js`, movido verbatim.
  // `priorityUpdateTick` no lo usa `filterAndSortChats` como argumento -- es la señal de
  // "recalcula, algo en localStorage cambió" que dispara `handlePriorityChange` (mismo propósito
  // que ya tenía antes de este paso `c`, cuando el filtro/sort no estaba memoizado y por eso
  // releía `localStorage` fresco en cada render sin necesitar una dep explícita).
  const filteredChats = React.useMemo(
    () => filterAndSortChats(chats.filter(c => c.chat_jid !== "status@broadcast"), searchTerm, activeFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chats, searchTerm, activeFilter, priorityUpdateTick],
  );

  return (
    <div 
      className="todo-panel whats-panel" 
      ref={panelRef}
      style={{ borderRadius: activeRightPanel ? "16px 0 0 16px" : "16px" }}
    >
      <button
        className="whats-floating-close"
        onClick={onClose}
        title="Cerrar Panel"
      >
        <X size={16} />
      </button>
      <div className="whats-panel-inner">
        <WhatsPanelTopBar
          showSettings={showSettings}
          onShowMessages={() => { setShowSettings(false); setSelectedChat(null); }}
          onShowSettings={() => { setShowSettings(true); setSelectedChat(null); }}
          onShowStories={() => { setShowSettings(false); handleSelectChat({ chat_jid: "status@broadcast", name: "Historias" }); }}
          selectedChat={selectedChat}
          activeRightPanel={activeRightPanel}
          onOpenProfile={() => setActiveRightPanel({ id: "user", icon: User, label: "Perfil del Usuario" })}
          isPinned={isPinned}
          onTogglePinned={() => setIsPinned(!isPinned)}
        />
        {/* Spacer Above Search (since action buttons were removed) */}
        {!selectedChat && <div style={{ height: "30px" }}></div>}

        {/* Body Content */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {showSettings ? (
            <WhatsSettingsView />
          ) : status !== "connected" ? (
            <WhatsConnectQrView status={status} qrCodeData={qrCodeData} qrProgress={qrProgress} onConnect={handleConnectClick} />
          ) : selectedChat ? (
            // Conversation View
            <div className="whats-conversation" style={{ flex: 1, minHeight: 0 }}>
              <WhatsConversationHeader
                chat={selectedChat}
                presences={presences}
                onOpenProfile={() => setActiveRightPanel({ id: "user", icon: User, label: "Perfil del Usuario" })}
                onBack={() => {
                  setSelectedChat(null);
                  setActiveRightPanel(null);
                }}
              />

              <WhatsMessageList
                selectedChatJid={selectedChat.chat_jid}
                messages={messages}
                loadingMessages={loadingMessages}
                loadingMore={loadingMore}
                chats={chats}
                isGroup={isGroupJid(selectedChat.chat_jid) || selectedChat.chat_jid === "status@broadcast"}
                scrollContainerRef={scrollContainerRef}
                onScroll={handleScroll}
                messagesEndRef={messagesEndRef}
                onReply={msgActions.setReplyingToMsg}
                onForward={msgActions.startForward}
                onOpenMedia={msgActions.openMediaPanel}
                onOpenTask={msgActions.openTaskPanel}
                onTaskFromMessage={handleTaskFromMessage}
                userWhatsappJid={user?.whatsapp_jid}
                meetMembers={meetMembers}
              />

              <WhatsSemanticTagBadges 
                tags={activeSemanticTags} 
                onMeetClick={() => setShowMeetPopup(prev => !prev)} 
                onTaskClick={() => setShowTaskPopup(prev => !prev)} 
              />

              {showMeetPopup && (
                <WhatsMeetPopup
                  searchQuery={meetSearchQuery}
                  onSearchChange={setMeetSearchQuery}
                  members={meetMembers}
                  selectedUser={selectedMeetUser}
                  onSelectUser={setSelectedMeetUser}
                  onClose={() => setShowMeetPopup(false)}
                  onPickOption={async (duration, user, optValue) => {
                    try {
                      const res = await apiClient.post('/api/booking/sessions/generate', {
                        hostUserId: user.id,
                        targetJid: selectedChat.chat_jid,
                        duration
                      });
                      if (res.success && res.session) {
                        const link = `https://colonny.xyz/p/${(user.nick || user.name || user.id).toString().toLowerCase().replace(/\s+/g, '')}?${optValue}/${res.session.shortCode}`;
                        setNewMessage(prev => prev ? `${prev}\n\n${link}` : link);
                      }
                    } catch (e) {
                      console.error("Error generating session link", e);
                    }
                    setShowMeetPopup(false);
                  }}
                />
              )}

              {showTaskPopup && (
                <WhatsTaskPopup
                  members={meetMembers}
                  onClose={() => setShowTaskPopup(false)}
                  onPickOption={async (taskData) => {
                    try {
                      // title, description, assignee, duration, checker
                      const payload = {
                        title: taskData.title,
                        description: taskData.description,
                        assignee: taskData.assignee?.nick || taskData.assignee?.name,
                        supervisor: taskData.checker?.nick || taskData.checker?.name,
                        duration_minutes: taskData.duration,
                        space_id: user?.currentSpace?.id,
                      };
                      const res = await apiClient.post('/api/tasks', payload);
                      if (res.success && res.task) {
                        const link = `https://colonny.xyz/t/${res.task.id}`;
                        setNewMessage(prev => prev ? `${prev}\n\n${link}` : link);
                      }
                    } catch (e) {
                      console.error("Error creating task", e);
                    }
                    setShowTaskPopup(false);
                  }}
                />
              )}

              <div style={{ position: "relative" }}>
                {msgActions.forwardingMsg && (
                  <WhatsForwardDrawer
                    chats={chats}
                    onForward={msgActions.doForward}
                    onClose={msgActions.cancelForward}
                  />
                )}
                <WhatsMessageComposer
                  chatJid={selectedChat.chat_jid}
                  isGroup={isGroupJid(selectedChat.chat_jid)}
                  isDragging={isDragging}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                  }}
                  onDrop={(e, action = 'attachment') => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                      const newFiles = Array.from(e.dataTransfer.files).map(file => ({ file, intent: action }));
                      setDroppedFiles(prev => [...prev, ...newFiles]);
                    }
                  }}
                  droppedFiles={droppedFiles}
                  onCancelDroppedFile={(index) => setDroppedFiles(prev => prev.filter((_, i) => i !== index))}
                  newMessage={newMessage}
                  onMessageChange={setNewMessage}
                  onSubmit={handleSendMessage}
                  onAttachClick={handleAttachClick}
                  sending={sending}
                  inputRef={inputRef}
                  replyingTo={msgActions.replyingToMsg}
                  onCancelReply={msgActions.clearReply}
                  uploadProgress={uploadProgress}
                />
              </div>
            </div>
          ) : (
            <WhatsChatList
              chats={filteredChats}
              loading={loading}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              activeFilter={activeFilter}
              onFilterChange={changeFilter}
              onSelectChat={handleSelectChat}
              scrollRef={chatListScrollRef}
            />
          )}
        </div>
      </div>

      {activeRightPanel && (
        <WhatsRightPanel
          activeRightPanel={activeRightPanel}
          chat={selectedChat}
          presences={presences}
          messages={messages}
          allChats={chats}
          width={msgActions.infoPanelWidth}
          onResize={msgActions.resizeInfoPanel}
          onClose={() => msgActions.setActiveRightPanel(null)}
          onPriorityChange={handlePriorityChange} onSelectChat={handleSelectChat}
          onOpenTask={msgActions.openTaskPanel}
        />
      )}

      {msgActions.standaloneTaskId && (
        <ComponentTaskHoy
          standaloneTaskId={msgActions.standaloneTaskId}
          user={user}
          toast={mockToast}
          onCloseStandalone={() => msgActions.setStandaloneTaskId(null)}
        />
      )}
    </div>
  );
};

export default WhatsPanel;
